import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatCurrency } from '../theme';
import StatusBadge from '../components/StatusBadge';
import api from '../services/api';

// ── Report tab types ──
type ReportTab = 'reconciliation' | 'disbursement' | 'category' | 'hospital' | 'sla';

const reportTitles: Record<ReportTab, { title: string; subtitle: string }> = {
  reconciliation: { title: 'Reconciliation Report', subtitle: 'Financial reconciliation and settlement tracking' },
  disbursement: { title: 'Disbursement Report', subtitle: 'Monthly payment breakdown and trends' },
  category: { title: 'Category-wise Report', subtitle: 'Spending analysis by beneficiary category' },
  hospital: { title: 'Hospital-wise Report', subtitle: 'Hospital utilization and variance analysis' },
  sla: { title: 'SLA Performance Report', subtitle: 'Service level compliance and turnaround analysis' },
};

const approvalTypes = [
  { label: 'All Types', value: 'all' },
  { label: 'Manual Approval', value: 'manual' },
  { label: 'Auto-Approved', value: 'auto' },
  { label: 'Emergency', value: 'emergency' },
];

const inputStyle: React.CSSProperties = {
  padding: '7px 12px', border: '1px solid #E2E8F0', borderRadius: 8,
  fontSize: 13, color: '#1E293B', background: '#fff', outline: 'none',
};
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

// ── Helpers ──
const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
const fmtMonth = (m: string) => {
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo, 10) - 1]} ${y}`;
};

// ══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════
const Reports: React.FC = () => {
  const { reportType } = useParams<{ reportType: string }>();
  const activeTab: ReportTab = (['reconciliation', 'disbursement', 'category', 'hospital', 'sla'].includes(reportType || '') ? reportType : 'reconciliation') as ReportTab;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data stores
  const [reconData, setReconData] = useState<any>(null);
  const [disbData, setDisbData] = useState<any>(null);
  const [catData, setCatData] = useState<any>(null);
  const [hospData, setHospData] = useState<any>(null);
  const [slaData, setSlaData] = useState<any>(null);

  // Reconciliation filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApproval, setFilterApproval] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Fetch data based on active tab
  useEffect(() => {
    setLoading(true);
    setError('');
    const fetch = async () => {
      try {
        switch (activeTab) {
          case 'reconciliation':
            if (!reconData) { const r = await api.dashboard.getReconciliationReport(); setReconData(r); }
            break;
          case 'disbursement':
            if (!disbData) { const r = await api.dashboard.getDisbursementReport(); setDisbData(r); }
            break;
          case 'category':
            if (!catData) { const r = await api.dashboard.getCategoryReport(); setCatData(r); }
            break;
          case 'hospital':
            if (!hospData) { const r = await api.dashboard.getHospitalReport(); setHospData(r); }
            break;
          case 'sla':
            if (!slaData) { const r = await api.dashboard.getSlaReport(); setSlaData(r); }
            break;
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line
  }, [activeTab]);

  // ── Reconciliation filtering ──
  const reconRows: any[] = reconData?.data || [];
  const reconFiltered = reconRows.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterApproval !== 'all') {
      if (filterApproval === 'emergency' && !r.isEmergency) return false;
      if (filterApproval === 'auto' && !r.approver?.startsWith('Auto')) return false;
      if (filterApproval === 'manual' && (r.isEmergency || r.approver?.startsWith('Auto'))) return false;
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      const refDate = r.approvedAt ? new Date(r.approvedAt) : r.submittedAt ? new Date(r.submittedAt) : null;
      if (!refDate || refDate < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo); to.setHours(23, 59, 59, 999);
      const refDate = r.approvedAt ? new Date(r.approvedAt) : r.submittedAt ? new Date(r.submittedAt) : null;
      if (!refDate || refDate > to) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      return r.refNumber?.toLowerCase().includes(q) || r.officerName?.toLowerCase().includes(q) || r.hospitalName?.toLowerCase().includes(q) || r.beneficiaryName?.toLowerCase().includes(q);
    }
    return true;
  });

  const reconSummary = {
    totalCases: reconFiltered.length,
    completedCases: reconFiltered.filter(r => r.status === 'completed').length,
    settlementPending: reconFiltered.filter(r => r.status === 'final_settlement').length,
    totalEstimated: reconFiltered.reduce((s, r) => s + r.estimatedAmount, 0),
    totalAdvancePaid: reconFiltered.reduce((s, r) => s + r.advancePaid, 0),
    totalActual: reconFiltered.filter(r => r.status === 'completed').reduce((s, r) => s + r.actualAmount, 0),
    totalBalanceDue: reconFiltered.filter(r => r.status === 'completed').reduce((s, r) => s + (r.balanceDue >= 0 ? r.balanceDue : 0), 0),
    totalRefunds: reconFiltered.filter(r => r.status === 'completed').reduce((s, r) => s + r.refundAmount, 0),
    totalDisbursed: reconFiltered.reduce((s, r) => s + r.advancePaid, 0) + reconFiltered.filter(r => r.status === 'completed').reduce((s, r) => s + (r.balanceDue >= 0 ? r.balanceDue : 0), 0),
  };

  const hasFilters = filterStatus !== 'all' || filterApproval !== 'all' || dateFrom || dateTo || search;
  const clearFilters = () => { setFilterStatus('all'); setFilterApproval('all'); setDateFrom(''); setDateTo(''); setSearch(''); };

  const exportCSV = (headers: string[], dataRows: any[][]) => {
    const csv = [headers, ...dataRows].map(r => r.map((c: any) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportRecon = () => {
    exportCSV(
      ['Ref', 'Status', 'Approval', 'Officer', 'Category', 'Beneficiary', 'Hospital', 'Estimated', 'Actual', '80% Advance', 'Balance/Refund', 'Variance %', 'Approved', 'Submitted'],
      reconFiltered.map(r => [r.refNumber, r.status, r.isEmergency ? 'Emergency' : r.approver?.startsWith('Auto') ? 'Auto' : 'Manual', r.officerName, r.category, r.beneficiaryName, r.hospitalName, r.estimatedAmount, r.actualAmount, r.advancePaid, r.isRefund ? -r.refundAmount : r.remainingPayment, r.variancePercent?.toFixed(1), r.approvedAt ? new Date(r.approvedAt).toLocaleDateString('en-IN') : '', r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-IN') : '']),
    );
  };

  // ── Stat card helper ──
  const StatCard = ({ icon, iconColor, value, label }: { icon: string; iconColor: string; value: string | number; label: string }) => (
    <div className="stat-card">
      <div className="stat-card-header">
        <div className={`stat-card-icon ${iconColor}`} dangerouslySetInnerHTML={{ __html: icon }} />
      </div>
      <h3>{value}</h3>
      <p>{label}</p>
    </div>
  );

  const FinCard = ({ label, value, color }: { label: string; value: string; color: string }) => (
    <div className="card" style={{ margin: 0 }}>
      <div className="card-body padded" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      </div>
    </div>
  );

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>{reportTitles[activeTab]?.title || 'Reports'}</h2>
            <p>{reportTitles[activeTab]?.subtitle || ''}</p>
          </div>
        </div>
        <div className="header-right">
          {activeTab === 'reconciliation' && (
            <div className="search-bar">
              <span>&#128269;</span>
              <input placeholder="Search ref, officer, hospital..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          )}
          <button className="header-btn primary" onClick={() => {
            if (activeTab === 'reconciliation') return exportRecon();
            // Generic export for other tabs
            const table = document.querySelector('.report-table table');
            if (!table) return;
            const ths = Array.from(table.querySelectorAll('thead th')).map(th => th.textContent || '');
            const trs = Array.from(table.querySelectorAll('tbody tr')).map(tr =>
              Array.from(tr.querySelectorAll('td')).map(td => td.textContent || '')
            );
            exportCSV(ths, trs);
          }}>&#8681; Export CSV</button>
        </div>
      </div>

      <div className="content">
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}

        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading report...</div>}

        {/* ═══════ RECONCILIATION TAB ═══════ */}
        {!loading && activeTab === 'reconciliation' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 20px', background: '#fff', borderRadius: 12, border: '1px solid #E8ECF2', marginBottom: 20 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filters</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#64748B' }}>From</span>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                <span style={{ fontSize: 12, color: '#64748B' }}>To</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ width: 1, height: 24, background: '#E8ECF2' }} />
              <select value={filterApproval} onChange={e => setFilterApproval(e.target.value)} style={selectStyle}>
                {approvalTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <div style={{ width: 1, height: 24, background: '#E8ECF2' }} />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                <option value="all">All Statuses</option>
                <option value="payment_80_done">80% Paid</option>
                <option value="final_settlement">Settlement</option>
                <option value="completed">Completed</option>
              </select>
              {hasFilters && <button onClick={clearFilters} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#64748B', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>Clear Filters</button>}
            </div>

            <div className="stats-grid">
              <StatCard icon="&#8377;" iconColor="green" value={formatCurrency(reconSummary.totalDisbursed)} label="Total Disbursed" />
              <StatCard icon="&#128203;" iconColor="blue" value={reconSummary.totalCases} label="Total Cases" />
              <StatCard icon="&#9203;" iconColor="gold" value={reconSummary.settlementPending} label="Settlement Pending" />
              <StatCard icon="&#10003;" iconColor="green" value={reconSummary.completedCases} label="Completed" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
              <FinCard label="Total Estimated" value={formatCurrency(reconSummary.totalEstimated)} color="#1E293B" />
              <FinCard label="80% Advance Paid" value={formatCurrency(reconSummary.totalAdvancePaid)} color="#059669" />
              <FinCard label="Total Actual Billed" value={formatCurrency(reconSummary.totalActual)} color="#1E293B" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
              <FinCard label="Balance Payments Due" value={formatCurrency(reconSummary.totalBalanceDue)} color="#F59E0B" />
              <FinCard label="Refunds Processed" value={formatCurrency(reconSummary.totalRefunds)} color="#EF4444" />
              <FinCard label="Showing" value={`${reconFiltered.length} / ${reconRows.length}`} color="#1E293B" />
            </div>

            {/* Table */}
            <div className="card">
              <div className="card-header"><h3>Reconciliation Details</h3></div>
              <div className="card-body">
                <div className="table-wrap report-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Reference</th><th>Officer</th><th>Hospital</th><th>Approval</th>
                        <th>Estimated</th><th>80% Advance</th><th>Actual</th><th>Balance</th><th>Variance</th><th>Date</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconFiltered.length === 0 ? (
                        <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>No records found</td></tr>
                      ) : reconFiltered.map(r => {
                        const aType = r.isEmergency ? 'Emergency' : r.approver?.startsWith('Auto') ? 'Auto' : 'Manual';
                        const aColor = r.isEmergency ? '#EF4444' : r.approver?.startsWith('Auto') ? '#8B5CF6' : '#3B82F6';
                        return (
                          <tr key={r.id}>
                            <td><div style={{ fontWeight: 700, fontSize: 12 }}>{r.refNumber}</div></td>
                            <td><div style={{ fontWeight: 600, fontSize: 13 }}>{r.officerName}</div><div style={{ fontSize: 11, color: '#94A3B8' }}>{r.beneficiaryName} &middot; {r.category}</div></td>
                            <td><div style={{ fontSize: 13 }}>{r.hospitalName}</div><div style={{ fontSize: 11, color: '#94A3B8' }}>{r.hospitalCity}</div></td>
                            <td><span style={{ padding: '2px 8px', borderRadius: 4, background: `${aColor}12`, color: aColor, fontSize: 11, fontWeight: 700 }}>{aType}</span></td>
                            <td className="amount">{formatCurrency(r.estimatedAmount)}</td>
                            <td className="amount" style={{ color: '#059669' }}>{formatCurrency(r.advancePaid)}</td>
                            <td className="amount">{r.actualAmount > 0 ? formatCurrency(r.actualAmount) : <span style={{ color: '#94A3B8' }}>—</span>}</td>
                            <td>{r.status === 'payment_80_done' ? <span style={{ fontSize: 12, color: '#94A3B8' }}>Awaiting</span> : r.isRefund ? <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', color: '#EF4444', fontSize: 12, fontWeight: 700 }}>&#8593; {formatCurrency(r.refundAmount)}</span> : <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.08)', color: '#F59E0B', fontSize: 12, fontWeight: 700 }}>&#8595; {formatCurrency(r.remainingPayment)}</span>}</td>
                            <td>{r.actualAmount > 0 ? <span style={{ fontSize: 12, fontWeight: 700, color: r.variancePercent > 10 ? '#EF4444' : r.variancePercent > 0 ? '#F59E0B' : '#059669' }}>{r.variancePercent > 0 ? '+' : ''}{r.variancePercent.toFixed(1)}%</span> : <span style={{ color: '#94A3B8', fontSize: 12 }}>—</span>}</td>
                            <td><div style={{ fontSize: 12 }}>{fmtDate(r.approvedAt)}</div></td>
                            <td><StatusBadge status={r.status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="pagination"><div className="pagination-info">Showing {reconFiltered.length} of {reconRows.length}</div></div>
              </div>
            </div>
          </>
        )}

        {/* ═══════ DISBURSEMENT TAB ═══════ */}
        {!loading && activeTab === 'disbursement' && disbData && (
          <>
            <div className="stats-grid">
              <StatCard icon="&#8377;" iconColor="green" value={formatCurrency(disbData.totals?.totalDisbursed || 0)} label="Total Disbursed" />
              <StatCard icon="&#128203;" iconColor="blue" value={disbData.totals?.count || 0} label="Total Cases" />
              <StatCard icon="&#128200;" iconColor="gold" value={formatCurrency(disbData.totals?.totalEstimated || 0)} label="Total Estimated" />
              <StatCard icon="&#128176;" iconColor="green" value={formatCurrency(disbData.totals?.totalAdvancePaid || 0)} label="Total Advance Paid" />
            </div>

            <div className="card">
              <div className="card-header"><h3>Monthly Disbursement Breakdown</h3></div>
              <div className="card-body">
                {/* Bar chart visualization */}
                {disbData.months?.length > 0 && (
                  <div style={{ padding: '20px 20px 0', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180 }}>
                      {disbData.months.map((m: any) => {
                        const maxVal = Math.max(...disbData.months.map((x: any) => x.totalDisbursed || 1));
                        const h = Math.max(((m.totalDisbursed || 0) / maxVal) * 150, 4);
                        return (
                          <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#1E293B' }}>{formatCurrency(m.totalDisbursed || 0)}</div>
                            <div style={{ width: '100%', maxWidth: 48, height: h, background: 'linear-gradient(180deg, #059669, #10B981)', borderRadius: '6px 6px 0 0' }} />
                            <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>{fmtMonth(m.month)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="table-wrap report-table">
                  <table>
                    <thead>
                      <tr><th>Month</th><th>Cases</th><th>Total Estimated</th><th>80% Advance</th><th>Total Actual</th><th>Total Disbursed</th></tr>
                    </thead>
                    <tbody>
                      {(disbData.months || []).map((m: any) => (
                        <tr key={m.month}>
                          <td style={{ fontWeight: 700 }}>{fmtMonth(m.month)}</td>
                          <td>{m.count}</td>
                          <td className="amount">{formatCurrency(m.totalEstimated || 0)}</td>
                          <td className="amount" style={{ color: '#059669' }}>{formatCurrency(m.totalAdvancePaid || 0)}</td>
                          <td className="amount">{formatCurrency(m.totalActual || 0)}</td>
                          <td className="amount" style={{ fontWeight: 700 }}>{formatCurrency(m.totalDisbursed || 0)}</td>
                        </tr>
                      ))}
                      {disbData.months?.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>No disbursement data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ═══════ CATEGORY TAB ═══════ */}
        {!loading && activeTab === 'category' && catData && (
          <>
            <div className="stats-grid">
              {(catData.categories || []).map((c: any) => (
                <div className="stat-card" key={c.category}>
                  <div className="stat-card-header">
                    <div className="stat-card-icon blue">{c.category?.charAt(0) || '?'}</div>
                  </div>
                  <h3>{c.count}</h3>
                  <p>{c.category} Cases</p>
                </div>
              ))}
            </div>

            <div className="card">
              <div className="card-header"><h3>Category-wise Breakdown</h3></div>
              <div className="card-body">
                {/* Visual bars */}
                {(catData.categories || []).length > 0 && (
                  <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                    {(catData.categories || []).map((c: any) => {
                      const colors: Record<string, string> = { IAS: '#3B82F6', Minister: '#8B5CF6', Judge: '#059669' };
                      const color = colors[c.category] || '#64748B';
                      return (
                        <div key={c.category} style={{ background: '#F8FAFC', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid #E8ECF2' }}>
                          <div style={{ width: 48, height: 48, borderRadius: 24, background: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 18, fontWeight: 800 }}>
                            {c.category?.charAt(0)}
                          </div>
                          <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{c.category}</div>
                          <div style={{ fontSize: 24, fontWeight: 800, color: '#1E293B', marginBottom: 4 }}>{formatCurrency(c.totalEstimated || 0)}</div>
                          <div style={{ fontSize: 12, color: '#64748B' }}>{c.count} cases &middot; {c.emergencyCount || 0} emergency</div>
                          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>Avg: {formatCurrency(c.avgAmount || 0)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="table-wrap report-table">
                  <table>
                    <thead>
                      <tr><th>Category</th><th>Cases</th><th>Total Estimated</th><th>Total Actual</th><th>80% Advance</th><th>Avg Amount</th><th>Emergency</th></tr>
                    </thead>
                    <tbody>
                      {(catData.categories || []).map((c: any) => (
                        <tr key={c.category}>
                          <td style={{ fontWeight: 700 }}>{c.category}</td>
                          <td>{c.count}</td>
                          <td className="amount">{formatCurrency(c.totalEstimated || 0)}</td>
                          <td className="amount">{formatCurrency(c.totalActual || 0)}</td>
                          <td className="amount" style={{ color: '#059669' }}>{formatCurrency(c.totalAdvancePaid || 0)}</td>
                          <td className="amount">{formatCurrency(c.avgAmount || 0)}</td>
                          <td>
                            {(c.emergencyCount || 0) > 0 ? (
                              <span className="badge emergency"><span className="badge-dot" />{c.emergencyCount}</span>
                            ) : '0'}
                          </td>
                        </tr>
                      ))}
                      {(catData.categories || []).length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ═══════ HOSPITAL TAB ═══════ */}
        {!loading && activeTab === 'hospital' && hospData && (
          <>
            <div className="stats-grid">
              <StatCard icon="&#127973;" iconColor="blue" value={(hospData.hospitals || []).length} label="Hospitals Used" />
              <StatCard icon="&#128203;" iconColor="gold" value={(hospData.hospitals || []).reduce((s: number, h: any) => s + h.count, 0)} label="Total LOCs" />
              <StatCard icon="&#8377;" iconColor="green" value={formatCurrency((hospData.hospitals || []).reduce((s: number, h: any) => s + (h.totalEstimated || 0), 0))} label="Total Amount" />
              <StatCard icon="&#128200;" iconColor="blue" value={`${((hospData.hospitals || []).reduce((s: number, h: any) => s + (h.avgVariancePercent || 0), 0) / Math.max((hospData.hospitals || []).length, 1)).toFixed(1)}%`} label="Avg Variance" />
            </div>

            <div className="card">
              <div className="card-header"><h3>Hospital-wise Analysis</h3></div>
              <div className="card-body">
                <div className="table-wrap report-table">
                  <table>
                    <thead>
                      <tr><th>#</th><th>Hospital</th><th>City</th><th>Tier</th><th>LOC Count</th><th>Total Estimated</th><th>Total Actual</th><th>Avg Amount</th><th>Avg Variance</th><th>Emergency</th></tr>
                    </thead>
                    <tbody>
                      {(hospData.hospitals || []).map((h: any, i: number) => (
                        <tr key={h.hospitalName + h.hospitalCity}>
                          <td style={{ fontWeight: 700, color: '#94A3B8' }}>{i + 1}</td>
                          <td style={{ fontWeight: 700 }}>{h.hospitalName}</td>
                          <td>{h.hospitalCity}</td>
                          <td><span style={{ padding: '2px 8px', borderRadius: 4, background: '#EEF2FF', color: '#4F46E5', fontSize: 11, fontWeight: 700 }}>Tier {h.tier}</span></td>
                          <td>{h.count}</td>
                          <td className="amount">{formatCurrency(h.totalEstimated || 0)}</td>
                          <td className="amount">{formatCurrency(h.totalActual || 0)}</td>
                          <td className="amount">{formatCurrency(h.avgEstimated || 0)}</td>
                          <td><span style={{ fontSize: 12, fontWeight: 700, color: (h.avgVariancePercent || 0) > 10 ? '#EF4444' : (h.avgVariancePercent || 0) > 0 ? '#F59E0B' : '#059669' }}>{(h.avgVariancePercent || 0) > 0 ? '+' : ''}{(h.avgVariancePercent || 0).toFixed(1)}%</span></td>
                          <td>{(h.emergencyCount || 0) > 0 ? <span className="badge emergency"><span className="badge-dot" />{h.emergencyCount}</span> : '0'}</td>
                        </tr>
                      ))}
                      {(hospData.hospitals || []).length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ═══════ SLA TAB ═══════ */}
        {!loading && activeTab === 'sla' && slaData && (
          <>
            <div className="stats-grid">
              <StatCard icon="&#128203;" iconColor="blue" value={slaData.summary?.totalCases || 0} label="Total Cases" />
              <StatCard icon="&#10003;" iconColor="green" value={slaData.summary?.withinSla || 0} label="Within SLA" />
              <StatCard icon="&#10060;" iconColor="gold" value={slaData.summary?.breached || 0} label="SLA Breached" />
              <StatCard icon="&#9203;" iconColor="blue" value={`${slaData.summary?.slaCompliancePercent?.toFixed(1) || 0}%`} label="SLA Compliance" />
            </div>

            {/* SLA Compliance Bar */}
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-body padded">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 8 }}>SLA Compliance Rate</div>
                    <div style={{ width: '100%', height: 24, background: '#F1F5F9', borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{
                        width: `${slaData.summary?.slaCompliancePercent || 0}%`,
                        height: '100%',
                        background: (slaData.summary?.slaCompliancePercent || 0) >= 90 ? 'linear-gradient(90deg, #059669, #10B981)' : (slaData.summary?.slaCompliancePercent || 0) >= 70 ? 'linear-gradient(90deg, #F59E0B, #FBBF24)' : 'linear-gradient(90deg, #EF4444, #F87171)',
                        borderRadius: 12,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 80 }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: (slaData.summary?.slaCompliancePercent || 0) >= 90 ? '#059669' : '#F59E0B' }}>
                      {slaData.summary?.slaCompliancePercent?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
              <FinCard label="Avg Turnaround" value={`${slaData.summary?.avgTurnaroundMinutes || 0} min`} color="#1E293B" />
              <FinCard label="Within SLA" value={`${slaData.summary?.withinSla || 0} cases`} color="#059669" />
              <FinCard label="Breached" value={`${slaData.summary?.breached || 0} cases`} color="#EF4444" />
            </div>

            {/* Emergency vs Standard breakdown */}
            <div className="card">
              <div className="card-header"><h3>Emergency vs Standard Breakdown</h3></div>
              <div className="card-body">
                <div className="table-wrap report-table">
                  <table>
                    <thead>
                      <tr><th>Type</th><th>Total Cases</th><th>Within SLA</th><th>Breached</th><th>Compliance %</th><th>Avg Turnaround</th></tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Standard', data: slaData.breakdown?.standard },
                        { label: 'Emergency', data: slaData.breakdown?.emergency },
                      ].map(({ label, data }) => data ? (
                        <tr key={label}>
                          <td style={{ fontWeight: 700 }}>
                            {label === 'Emergency' && <span className="badge emergency" style={{ marginRight: 6 }}><span className="badge-dot" />EMG</span>}
                            {label}
                          </td>
                          <td>{data.totalCases}</td>
                          <td style={{ color: '#059669', fontWeight: 600 }}>{data.withinSla}</td>
                          <td style={{ color: data.breached > 0 ? '#EF4444' : '#94A3B8', fontWeight: 600 }}>{data.breached}</td>
                          <td>
                            <span style={{
                              padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                              background: data.slaCompliancePercent >= 90 ? 'rgba(5,150,105,0.08)' : 'rgba(239,68,68,0.08)',
                              color: data.slaCompliancePercent >= 90 ? '#059669' : '#EF4444',
                            }}>
                              {data.slaCompliancePercent?.toFixed(1)}%
                            </span>
                          </td>
                          <td>{data.avgTurnaroundMinutes || 0} min</td>
                        </tr>
                      ) : null)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Reports;
